'use client';

import { useCallback, useEffect, useState } from 'react';

import { CheckCircle2, Loader2, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@kit/ui/utils';
import { formatTimeToAmPm } from '@kit/shared/utils';

import { DateStrip } from '../book/_components/date-strip';
import { TrainerSelection, type TrainerResource } from '../book/_components/trainer-selection';
import { getAllBookingSlotsUnified } from '../book/_lib/server/server-actions';
import type { IntakeCustomField } from '../_lib/schema/intake.schema';

/** Selection captured by the tour field — everything submit needs to book. */
export interface TourSelection {
  serviceId: string;
  courtId: string;
  trainerId: string;
  bookingDate: string; // YYYY-MM-DD
  startTime: string; // HH:MM
  endTime: string; // HH:MM
  slotDuration: number;
  isPeak: boolean;
}

// available_resources rows already match TrainerResource
type SlotResource = TrainerResource;

interface Slot {
  start_time: string;
  end_time: string;
  is_available: boolean;
  is_peak?: boolean;
  available_court_ids?: string[];
  available_resources?: SlotResource[];
}

interface IntakeTourFieldProps {
  field: IntakeCustomField;
  disabled?: boolean;
  error?: string;
  value?: TourSelection;
  onChange: (value: TourSelection | undefined) => void;
  language?: string;
}

function formatDateKey(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function durationMinutes(start: string, end: string): number {
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  if ([sh, sm, eh, em].some((n) => n == null || Number.isNaN(n))) return 30;
  let diff = (eh! * 60 + em!) - (sh! * 60 + sm!);
  if (diff <= 0) diff += 24 * 60; // crosses midnight
  return diff;
}

/**
 * Embedded tour-booking widget for the membership intake form. Bound to a
 * service (slot_duration_template) via the custom field's `options[0]`.
 * Pick a date → time → guide; the selection is lifted to the form and the
 * actual (free) booking is created on intake submit.
 */
export function IntakeTourField({
  field,
  disabled,
  error,
  value,
  onChange,
  language = 'en',
}: IntakeTourFieldProps) {
  const { t } = useTranslation();
  const serviceId = field.options?.[0];

  const [selectedDate, setSelectedDate] = useState<Date>(() => new Date());
  const [slots, setSlots] = useState<Slot[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeStart, setActiveStart] = useState<string | null>(null);

  const loadSlots = useCallback(
    async (date: Date) => {
      if (!serviceId) return;
      setLoading(true);
      setActiveStart(null);
      try {
        const result = await getAllBookingSlotsUnified(
          serviceId,
          formatDateKey(date),
          'trainer',
        );
        if (result.success && result.data) {
          setSlots(
            (result.data as unknown as Slot[]).filter((s) => s.is_available),
          );
        } else {
          setSlots([]);
        }
      } catch {
        setSlots([]);
      } finally {
        setLoading(false);
      }
    },
    [serviceId],
  );

  useEffect(() => {
    void loadSlots(selectedDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceId]);

  const handleDateSelect = (date: Date) => {
    setSelectedDate(date);
    onChange(undefined); // changing date invalidates any prior pick
    void loadSlots(date);
  };

  const activeSlot = slots.find((s) => s.start_time === activeStart);

  if (!serviceId) {
    return (
      <div className="rounded-lg border border-dashed p-4 text-center text-sm text-muted-foreground">
        {t('marketing:intakeTour.notConfigured')}
      </div>
    );
  }

  // Already-picked summary
  if (value) {
    const guide = activeSlot?.available_resources?.find((r) => r.id === value.trainerId);
    return (
      <div className="space-y-1">
        <p className="text-sm font-semibold">{field.label}</p>
        {field.description && (
          <p className="text-xs text-muted-foreground">{field.description}</p>
        )}
        <div className="mt-2 flex items-center justify-between rounded-lg border bg-green-50 p-3">
          <div className="flex items-center gap-2 text-sm text-green-700">
            <CheckCircle2 className="h-4 w-4 shrink-0" />
            <span>
              {value.bookingDate} · {formatTimeToAmPm(value.startTime, language)}
              {guide ? ` · ${guide.name}` : ''}
            </span>
          </div>
          <button
            type="button"
            onClick={() => onChange(undefined)}
            disabled={disabled}
            className="rounded-full p-1 text-green-700/70 transition-colors hover:bg-green-100 hover:text-green-700"
            aria-label={t('marketing:intakeTour.clearSelectionAriaLabel')}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div>
        <p className="text-sm font-semibold">{field.label}</p>
        {field.description && (
          <p className="text-xs text-muted-foreground">{field.description}</p>
        )}
      </div>

      <DateStrip
        selected={selectedDate}
        onSelect={handleDateSelect}
        language={language}
      />

      {loading ? (
        <div className="flex items-center justify-center py-6 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : slots.length === 0 ? (
        <p className="py-4 text-center text-sm text-muted-foreground">
          {t('marketing:intakeTour.noTimes')}
        </p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {slots.map((slot) => (
            <button
              key={slot.start_time}
              type="button"
              disabled={disabled}
              onClick={() => setActiveStart(slot.start_time)}
              className={cn(
                'rounded-lg border px-3 py-2 text-sm transition-colors',
                activeStart === slot.start_time
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'hover:bg-muted',
              )}
            >
              {formatTimeToAmPm(slot.start_time, language)}
            </button>
          ))}
        </div>
      )}

      {activeSlot && (activeSlot.available_resources?.length ?? 0) > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {t('marketing:intakeTour.chooseGuide')}
          </p>
          <TrainerSelection
            trainers={activeSlot.available_resources ?? []}
            isPeakTime={activeSlot.is_peak ?? false}
            // A picked tour returns early above (summary view), so no guide is
            // pre-selected here.
            selectedTrainerId=""
            onTrainerSelect={(trainerId) => {
              const courtId = activeSlot.available_court_ids?.[0];
              if (!courtId) return;
              onChange({
                serviceId,
                courtId,
                trainerId,
                bookingDate: formatDateKey(selectedDate),
                startTime: activeSlot.start_time,
                endTime: activeSlot.end_time,
                slotDuration: durationMinutes(
                  activeSlot.start_time,
                  activeSlot.end_time,
                ),
                isPeak: activeSlot.is_peak ?? false,
              });
            }}
          />
        </div>
      )}

      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  );
}
